import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StoreFloor } from "@/pages/StoreHeatMap";
import { api } from "@/lib/api";
import { toggleFullscreen } from "@/lib/tv";
import {
  Fire, Users, CornersOut, Gear, Check, X, ShieldCheck, FloppyDisk, WarningDiamond,
} from "@phosphor-icons/react";

const PER_SLIDE = 4;
const chunk = (arr, n) => arr.reduce((a, _, i) => (i % n ? a : [...a, arr.slice(i, i + n)]), []);

// 4-band customer-traffic heat: blue (quiet) -> green -> orange -> red (hot).
const HEAT_BANDS = [
  { min: 0.62, rgb: "255,59,48", label: "Hot", dot: "#FF3B30" },
  { min: 0.38, rgb: "255,159,10", label: "Busy", dot: "#FF9F0A" },
  { min: 0.16, rgb: "48,215,90", label: "Steady", dot: "#32D74B" },
  { min: 0.04, rgb: "10,132,255", label: "Quiet", dot: "#0A84FF" },
];
const bandOf = (v) => HEAT_BANDS.find((b) => v >= b.min) || null;

// Animated color-coded heat the AI reads over the real camera image.
function HeatOverlay({ heat }) {
  if (!heat || !heat.cells || !heat.max) return null;
  const { cells, gw, gh } = heat;
  return (
    <>
      <div className="absolute inset-0 pointer-events-none"
        style={{ display: "grid", gridTemplateColumns: `repeat(${gw}, 1fr)`, gridTemplateRows: `repeat(${gh}, 1fr)` }}>
        {cells.flatMap((row, r) =>
          row.map((v, c) => {
            const b = bandOf(v);
            if (!b) return <div key={`${r}-${c}`} />;
            const hot = v >= 0.62;
            return (
              <div key={`${r}-${c}`} className={hot ? "hz-cell-hot" : ""}
                style={{ background: `rgba(${b.rgb},${0.16 + v * 0.5})`, filter: "blur(7px)" }} />
            );
          })
        )}
      </div>
      {/* flowing pathway sweep */}
      <div className="absolute inset-0 pointer-events-none hz-sweep" />
    </>
  );
}

function Tile({ cam, frame, count, heat, flagged }) {
  return (
    <div data-testid={`hz-tile-${cam.id}`}
      className={`relative bg-black overflow-hidden w-full h-full border ${flagged ? "border-[#FF3B30] shadow-[0_0_0_2px_#FF3B30]" : "border-white/10"}`}>
      {frame ? (
        <img src={`data:image/jpeg;base64,${frame}`} alt={cam.name} className="w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-[#3f3f46] font-mono-data text-xs">connecting…</div>
      )}
      <HeatOverlay heat={heat} />
      <div className="absolute top-2 left-2 flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-[#32D74B] live-dot" />
        <span className="font-mono-data text-xs tracking-wide bg-black/60 px-2 py-0.5 text-white uppercase">{cam.name}</span>
      </div>
      <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/70 px-2 py-0.5">
        <Users size={13} weight="fill" className={count > 0 ? "text-[#FF9F0A]" : "text-[#52525B]"} />
        <span className="font-mono-data text-xs font-bold text-white">{count ?? 0}</span>
      </div>
      {flagged && (
        <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-[#FF3B30] px-2 py-0.5 animate-pulse">
          <WarningDiamond size={13} weight="fill" />
          <span className="font-mono-data text-[11px] font-bold uppercase text-white">Alert</span>
        </div>
      )}
      <span className="absolute bottom-1.5 right-2 font-display font-bold text-[10px] tracking-[0.2em] uppercase text-[#0A84FF]" style={{ textShadow: "0 1px 3px #000" }}>A V Tech</span>
    </div>
  );
}

// Reusable colour key so staff can read the board at a glance.
function HeatLegend() {
  return (
    <div data-testid="hz-legend" className="flex items-center gap-3">
      {HEAT_BANDS.map((b) => (
        <span key={b.label} className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full" style={{ background: b.dot, boxShadow: `0 0 8px ${b.dot}` }} />
          <span className="font-mono-data text-[11px] uppercase tracking-wide text-white/80">{b.label}</span>
        </span>
      ))}
    </div>
  );
}

export default function HotZoneTv() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("sv_token"));
  const [checking, setChecking] = useState(true);
  const [code, setCode] = useState("");
  const [codeErr, setCodeErr] = useState("");

  const [allCams, setAllCams] = useState([]);
  const [selected, setSelected] = useState([]);       // ordered camera ids
  const [flipSeconds, setFlipSeconds] = useState(15);
  const [frames, setFrames] = useState({});
  const [counts, setCounts] = useState({});
  const [heat, setHeat] = useState({});
  const [slide, setSlide] = useState(0);
  const [editing, setEditing] = useState(false);
  const [now, setNow] = useState(new Date());
  const [portrait, setPortrait] = useState(typeof window !== "undefined" && window.innerHeight > window.innerWidth);
  const freshRef = useRef(Date.now());

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const onR = () => setPortrait(window.innerHeight > window.innerWidth);
    window.addEventListener("resize", onR); window.addEventListener("orientationchange", onR); onR();
    return () => { window.removeEventListener("resize", onR); window.removeEventListener("orientationchange", onR); };
  }, []);

  // Keep TV awake.
  useEffect(() => {
    let lock;
    const acquire = async () => { try { lock = await navigator.wakeLock?.request("screen"); } catch { /* noop */ } };
    acquire();
    const onVis = () => { if (document.visibilityState === "visible") acquire(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); try { lock && lock.release(); } catch { /* noop */ } };
  }, []);

  const loadConfig = useCallback(async () => {
    const { data } = await api.get("/hotzone-tv/config");
    setSelected(data.cameras || []);
    setFlipSeconds(data.flip_seconds || 15);
  }, []);

  const loadCams = useCallback(async () => {
    const { data } = await api.get("/cameras");
    setAllCams((data || []).filter((c) => c.status === "online"));
  }, []);

  // Verify the stored token still works; otherwise show the TV code gate.
  useEffect(() => {
    let ok = true;
    (async () => {
      if (!localStorage.getItem("sv_token")) { setAuthed(false); setChecking(false); return; }
      try {
        await Promise.all([loadConfig(), loadCams()]);
        if (ok) setAuthed(true);
      } catch {
        localStorage.removeItem("sv_token");
        if (ok) setAuthed(false);
      } finally {
        if (ok) setChecking(false);
      }
    })();
    return () => { ok = false; };
  }, [loadConfig, loadCams]);

  const submitCode = async (e) => {
    e.preventDefault();
    setCodeErr("");
    try {
      const { data } = await api.post("/auth/login", { pin: code.trim() });
      localStorage.setItem("sv_token", data.token);
      await Promise.all([loadConfig(), loadCams()]);
      setAuthed(true);
    } catch {
      setCodeErr("Invalid TV code — try again.");
    }
  };

  const selectedCams = useMemo(
    () => selected.map((id) => allCams.find((c) => c.id === id)).filter(Boolean),
    [selected, allCams]
  );
  const camSlides = useMemo(() => chunk(selectedCams, PER_SLIDE), [selectedCams]);
  // Last slide = whole-store FLOOR MAP so staff see which product spots draw the most customers.
  const slides = useMemo(() => [...camSlides, "FLOOR"], [camSlides]);
  const slideCount = slides.length;
  const isFloor = slides[slide % slideCount] === "FLOOR";
  const activeSlide = isFloor ? [] : (slides[slide % slideCount] || []);

  // Auto-flip between slides.
  useEffect(() => {
    if (slideCount <= 1 || editing) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % slideCount), flipSeconds * 1000);
    return () => clearInterval(t);
  }, [slideCount, flipSeconds, editing]);
  useEffect(() => { if (slide >= slideCount) setSlide(0); }, [slideCount, slide]);

  // Poll live frames for the CURRENT slide only (keeps load light).
  useEffect(() => {
    if (!authed || activeSlide.length === 0) return;
    let cancelled = false;
    const pull = async () => {
      await Promise.all(activeSlide.map(async (cam) => {
        try {
          const r = await api.get(`/cameras/${cam.id}/frame?display=1&w=1920`);
          if (!cancelled && r.data?.b64) {
            setFrames((p) => ({ ...p, [cam.id]: r.data.b64 }));
            freshRef.current = Date.now();
          }
        } catch { /* no frame yet */ }
      }));
    };
    pull();
    const t = setInterval(pull, 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, [authed, slide, activeSlide]);

  // Heat grid for current slide cameras, refreshed on each flip.
  useEffect(() => {
    if (!authed || activeSlide.length === 0) return;
    let cancelled = false;
    (async () => {
      await Promise.all(activeSlide.map(async (cam) => {
        try {
          const r = await api.get(`/analytics/heatmap/${cam.id}?period=7d`);
          if (!cancelled) setHeat((p) => ({ ...p, [cam.id]: { cells: r.data.cells, gw: r.data.gw, gh: r.data.gh, max: r.data.max } }));
        } catch { /* skip */ }
      }));
    })();
    return () => { cancelled = true; };
  }, [authed, slide, activeSlide]);

  // Live people counts + alert flags (whole board, every 8s).
  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    const pull = async () => {
      try {
        const { data } = await api.get("/wall/state");
        if (cancelled) return;
        const c = {}, f = {};
        (data.tiles || []).forEach((t) => { c[t.id] = t.people_count || 0; });
        (data.alerts || []).forEach((a) => { if (a.camera_id) f[a.camera_id] = true; });
        setCounts(c);
        setCounts((prev) => ({ ...prev, __flags: f }));
      } catch { /* skip */ }
    };
    pull();
    const t = setInterval(pull, 8000);
    return () => { cancelled = true; clearInterval(t); };
  }, [authed]);

  const toggleCam = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const saveConfig = async () => {
    try {
      await api.put("/hotzone-tv/config", { cameras: selected, flip_seconds: flipSeconds });
      setEditing(false);
      setSlide(0);
    } catch { /* master only */ }
  };

  const clock = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  // ---- TV code gate ----
  if (!authed) {
    return (
      <div data-testid="hz-gate" className="fixed inset-0 bg-[#0A0A0C] flex items-center justify-center p-6">
        <form onSubmit={submitCode} className="w-full max-w-sm bg-[#121214] border border-white/10 p-6">
          <div className="flex items-center gap-2 mb-1">
            <Fire size={22} weight="fill" className="text-[#FF9F0A]" />
            <h1 className="font-display font-black uppercase tracking-tighter text-lg text-white">Hot Zone TV</h1>
          </div>
          <p className="text-xs text-[#A1A1AA] mb-4">Enter the TV display code to start the board.</p>
          <input
            data-testid="hz-code-input"
            autoFocus type="password" inputMode="numeric" value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="TV code"
            className="w-full bg-[#0A0A0C] border border-white/15 px-4 py-3 text-white font-mono-data text-lg tracking-[0.3em] text-center outline-none focus:border-[#FF9F0A]"
          />
          {codeErr && <p data-testid="hz-code-err" className="text-[#FF3B30] text-xs mt-2">{codeErr}</p>}
          <button data-testid="hz-code-submit" type="submit"
            className="mt-4 w-full bg-[#FF9F0A] text-black font-display font-bold uppercase tracking-wide py-3 hover:bg-[#FFB340] transition-colors">
            Start board
          </button>
          <p className="flex items-center gap-1.5 justify-center font-mono-data text-[10px] tracking-[0.2em] text-[#3f3f46] uppercase mt-5">
            <ShieldCheck size={12} weight="fill" /> Powered by A V Tech
          </p>
        </form>
      </div>
    );
  }

  if (checking) {
    return <div className="fixed inset-0 bg-black flex items-center justify-center text-[#52525B] font-mono-data text-sm">loading…</div>;
  }

  const flags = counts.__flags || {};

  return (
    <div data-testid="hotzone-tv" className="fixed inset-0 bg-black flex flex-col select-none">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-b from-black/90 to-transparent z-20">
        <div className="flex items-center gap-2.5">
          <Fire size={24} weight="fill" className="text-[#FF9F0A]" />
          <div>
            <p className="font-display font-black uppercase tracking-tighter text-white text-lg leading-none">Hot Zone TV</p>
            <p className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-[#0A84FF] mt-0.5">
              Slide {(slide % slideCount) + 1}/{slideCount} · {activeSlide.map((c) => c.zone).filter((v, i, a) => a.indexOf(v) === i).slice(0, 4).join(" · ") || "—"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <HeatLegend />
          <span className="flex items-center gap-2 font-mono-data text-sm text-[#32D74B]">
            <span className="w-2.5 h-2.5 rounded-full bg-[#32D74B] live-dot" /> LIVE · {selectedCams.length} CAM
          </span>
          <span data-testid="hz-clock" className="font-mono-data text-2xl font-medium text-white tabular-nums">{clock}</span>
          <button data-testid="hz-edit-toggle" onClick={() => setEditing((e) => !e)} title="Pick cameras"
            className="w-10 h-10 flex items-center justify-center bg-[#121214]/90 border border-white/20 text-white hover:border-[#FF9F0A]/60">
            <Gear size={18} weight="bold" />
          </button>
          <button data-testid="hz-fullscreen" onClick={() => toggleFullscreen(document.documentElement)} title="Fullscreen"
            className="w-10 h-10 flex items-center justify-center bg-[#007AFF] text-white hover:bg-[#3395FF]">
            <CornersOut size={18} weight="bold" />
          </button>
        </div>
      </div>

      {/* Camera grid (flipping slide) */}
      <div className="flex-1 min-h-0 p-1 relative" style={{ perspective: "1600px" }}>
        {isFloor ? (
          <div key="floor" data-testid="hz-floor-slide" className="w-full h-full overflow-hidden p-3 hz-flip bg-[#0A0A0C]">
            <p className="font-display font-black text-white text-2xl mb-2">Where do customers stand the most? <span className="text-[#71717A] text-sm font-mono-data">today · time spent</span></p>
            <StoreFloor period="today" metric="time" height="calc(100vh - 260px)" refreshMs={300000} />
          </div>
        ) : selectedCams.length === 0 ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-center">
            <Fire size={40} weight="duotone" className="text-[#FF9F0A]" />
            <p className="font-display font-bold text-white text-lg">No cameras picked yet</p>
            <p className="text-[#A1A1AA] text-sm max-w-md">Tap the gear (top-right) and tick the cameras you want on the Hot Zone board. 4 cameras fill each slide.</p>
            <button data-testid="hz-empty-pick" onClick={() => setEditing(true)} className="mt-2 bg-[#FF9F0A] text-black font-display font-bold uppercase text-sm px-5 py-2.5">Pick cameras</button>
          </div>
        ) : (
          <div key={slide} data-testid="hz-slide" className="w-full h-full grid gap-1 hz-flip"
            style={{ gridTemplateColumns: `repeat(2, minmax(0,1fr))`, gridTemplateRows: `repeat(2, minmax(0,1fr))` }}>
            {activeSlide.map((cam) => (
              <Tile key={cam.id} cam={cam} frame={frames[cam.id]} count={counts[cam.id]} heat={heat[cam.id]} flagged={!!flags[cam.id]} />
            ))}
          </div>
        )}
      </div>

      {/* Slide dots */}
      {slideCount > 1 && (
        <div className="flex items-center justify-center gap-1.5 py-1.5 bg-black">
          {slides.map((_, i) => (
            <button key={i} data-testid={`hz-dot-${i}`} onClick={() => setSlide(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === (slide % slideCount) ? "w-7 bg-[#FF9F0A]" : "w-1.5 bg-white/25"}`} />
          ))}
        </div>
      )}

      {/* Edit drawer */}
      {editing && (
        <div data-testid="hz-edit-drawer" className="absolute top-0 right-0 bottom-0 w-full max-w-sm bg-[#0A0A0C] border-l border-white/15 z-40 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <h2 className="font-display font-black uppercase tracking-tight text-white text-sm flex items-center gap-2">
              <Gear size={16} weight="bold" className="text-[#FF9F0A]" /> Hot Zone cameras ({selected.length})
            </h2>
            <button data-testid="hz-edit-close" onClick={() => setEditing(false)} className="text-[#A1A1AA] hover:text-white"><X size={20} /></button>
          </div>
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
            <span className="font-mono-data text-[11px] uppercase tracking-wider text-[#94A3B8]">Flip every</span>
            <select data-testid="hz-flip-select" value={flipSeconds} onChange={(e) => setFlipSeconds(Number(e.target.value))}
              className="bg-[#121214] border border-white/15 text-white font-mono-data text-sm px-2 py-1 outline-none">
              {[8, 12, 15, 20, 30].map((s) => <option key={s} value={s}>{s}s</option>)}
            </select>
            <span className="font-mono-data text-[11px] text-[#52525B] ml-auto">4 / slide · {Math.max(1, Math.ceil(selected.length / PER_SLIDE))} slides</span>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {allCams.map((c) => {
              const on = selected.includes(c.id);
              return (
                <button key={c.id} data-testid={`hz-pick-${c.id}`} onClick={() => toggleCam(c.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 border text-left ${on ? "border-[#FF9F0A]/60 bg-[#FF9F0A]/10" : "border-white/10 bg-[#121214] hover:border-white/25"}`}>
                  <span className={`w-5 h-5 shrink-0 flex items-center justify-center border ${on ? "bg-[#FF9F0A] border-[#FF9F0A]" : "border-white/30"}`}>
                    {on && <Check size={14} weight="bold" className="text-black" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-display font-bold text-sm text-white truncate">{c.name}</span>
                    <span className="block font-mono-data text-[11px] text-[#64748B] truncate">{c.zone}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <div className="p-3 border-t border-white/10">
            <button data-testid="hz-save" onClick={saveConfig}
              className="w-full flex items-center justify-center gap-2 bg-[#FF9F0A] text-black font-display font-bold uppercase tracking-wide py-3 hover:bg-[#FFB340]">
              <FloppyDisk size={18} weight="bold" /> Save board
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes hzFlip { from { opacity: 0; transform: rotateY(12deg) translateX(40px); } to { opacity: 1; transform: rotateY(0) translateX(0); } }
        .hz-flip { animation: hzFlip 600ms cubic-bezier(.2,.7,.2,1); transform-origin: center left; }
        @keyframes hzPulse { 0%,100% { opacity: .5; } 50% { opacity: .95; } }
        .hz-cell-hot { animation: hzPulse 1.6s ease-in-out infinite; }
        @keyframes hzSweep { 0% { transform: translateX(-130%); } 100% { transform: translateX(130%); } }
        .hz-sweep { background: linear-gradient(105deg, transparent 42%, rgba(255,255,255,.07) 50%, transparent 58%); animation: hzSweep 4.5s linear infinite; mix-blend-mode: screen; }
      `}</style>
    </div>
  );
}
